import { useState } from 'react'
import { startOfDay, addDays, isWeekend } from 'date-fns'
import { hasAvailableSlots } from './timeSlots'

// The flow moves through a single lifecycle status instead of juggling separate
// boolean flags, which keeps impossible combinations (e.g. confirmed + error) unrepresentable.
export type SelfScheduleStatus = 'idle' | 'submitting' | 'error' | 'confirmed'

type ConfirmHandler = (date: Date, time: string) => void | Promise<void>

export interface SelfScheduleState {
  selectedDate: Date | null
  selectedTime: string | null
  status: SelfScheduleStatus
  isSubmitting: boolean
  showConfirmation: boolean
  isConfirmDisabled: boolean
  selectDate: (date: Date) => void
  selectTime: (time: string) => void
  confirm: () => Promise<void>
  reset: () => void
}

// Find the first weekday with available slots. Weekends are skipped so the
// default selection matches the weekday-only WeekDatePicker (e.g. on a weekend
// this lands on Monday). Scans a full week, which always reaches a future
// weekday that has slots.
const getFirstAvailableDate = (startDate: Date): Date => {
  let candidate = startDate
  for (let i = 0; i <= 7; i++) {
    if (!isWeekend(candidate) && hasAvailableSlots(candidate)) {
      return candidate
    }
    candidate = addDays(candidate, 1)
  }

  return startDate
}

/**
 * Owns the self-schedule lifecycle: the selected date/time, the request status,
 * and the transitions between them. Kept free of any DOM/dialog concerns so it
 * can be unit-tested in isolation.
 */
export const useSelfSchedule = (onConfirm: ConfirmHandler): SelfScheduleState => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => getFirstAvailableDate(startOfDay(new Date())))
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [status, setStatus] = useState<SelfScheduleStatus>('idle')

  const selectDate = (date: Date) => {
    setSelectedDate(date)
    setStatus('idle') // dismiss a prior submission error
  }

  const selectTime = (time: string) => {
    setSelectedTime(time)
    setStatus('idle') // dismiss a prior submission error
  }

  const confirm = async () => {
    if (!selectedDate || !selectedTime) {
      return
    }

    setStatus('submitting')

    try {
      await onConfirm(selectedDate, selectedTime)
      setStatus('confirmed')
    } catch {
      setStatus('error')
    }
  }

  const reset = () => {
    setSelectedDate(getFirstAvailableDate(startOfDay(new Date())))
    setSelectedTime(null)
    setStatus('idle')
  }

  return {
    selectedDate,
    selectedTime,
    status,
    isSubmitting: status === 'submitting',
    showConfirmation: status === 'confirmed',
    isConfirmDisabled: !selectedDate || !selectedTime,
    selectDate,
    selectTime,
    confirm,
    reset
  }
}
